(function () {
  'use strict';

  var MEDIAPIPE_VERSION = '0.10.14';
  var MODEL_URL =
    'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

  // Average ratio between a glasses frame's temple-to-temple width and a
  // person's inter-pupillary distance. Real per-frame widths aren't stored in
  // the catalog (only photos), so this is an approximation — good enough for
  // a visual try-on, not a precision fit measurement.
  var FRAME_TO_EYE_DISTANCE_RATIO = 2.2;

  var faceLandmarkerPromise = null;
  var rafId = null;
  var stream = null;
  var currentModal = null;

  function loadFaceLandmarker() {
    if (faceLandmarkerPromise) return faceLandmarkerPromise;
    faceLandmarkerPromise = import(
      'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MEDIAPIPE_VERSION
    ).then(function (vision) {
      return vision.FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@' + MEDIAPIPE_VERSION + '/wasm'
      ).then(function (filesetResolver) {
        return vision.FaceLandmarker.createFromOptions(filesetResolver, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
          runningMode: 'VIDEO',
          numFaces: 1,
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false
        });
      });
    });
    return faceLandmarkerPromise;
  }

  function setStatus(modal, text, state) {
    var el = modal.querySelector('[data-tryon-status]');
    if (!el) return;
    el.textContent = text || '';
    if (state) {
      el.setAttribute('data-state', state);
    } else {
      el.removeAttribute('data-state');
    }
  }

  function openModal(modal) {
    currentModal = modal;
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    setStatus(modal, 'Iniciando câmera…');

    var video = modal.querySelector('.tryon-video');
    var glasses = modal.querySelector('.tryon-glasses-img');

    Promise.all([
      loadFaceLandmarker(),
      navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      })
    ])
      .then(function (results) {
        if (currentModal !== modal) return; // closed while loading
        var faceLandmarker = results[0];
        stream = results[1];
        video.srcObject = stream;
        return video.play().then(function () {
          setStatus(modal, '', 'ready');
          startLoop(modal, video, glasses, faceLandmarker);
        });
      })
      .catch(function (err) {
        if (currentModal !== modal) return;
        console.error('[tryon]', err);
        var message = 'Não foi possível acessar a câmera.';
        if (err && err.name === 'NotAllowedError') {
          message = 'Permissão de câmera negada. Habilite o acesso à câmera no navegador e tente de novo.';
        } else if (err && err.name === 'NotFoundError') {
          message = 'Nenhuma câmera encontrada neste dispositivo.';
        }
        setStatus(modal, message, 'error');
      });
  }

  function startLoop(modal, video, glasses, faceLandmarker) {
    var bboxWFrac = parseFloat(modal.dataset.bboxWFrac) || 0.9;
    var bboxCxFrac = parseFloat(modal.dataset.bboxCxFrac) || 0.5;
    var bboxCyFrac = parseFloat(modal.dataset.bboxCyFrac) || 0.45;
    var lastNoFaceNotice = 0;

    function frame() {
      if (currentModal !== modal || video.readyState < 2 || !glasses.naturalWidth) {
        rafId = requestAnimationFrame(frame);
        return;
      }

      var result = faceLandmarker.detectForVideo(video, performance.now());
      var landmarks = result && result.faceLandmarks && result.faceLandmarks[0];

      if (!landmarks) {
        glasses.style.opacity = '0';
        var now = performance.now();
        if (now - lastNoFaceNotice > 1500) {
          setStatus(modal, 'Centralize seu rosto na câmera', 'ready');
          lastNoFaceNotice = now;
        }
        rafId = requestAnimationFrame(frame);
        return;
      }

      setStatus(modal, '', 'ready');
      glasses.style.opacity = '1';

      var vw = video.videoWidth;
      var vh = video.videoHeight;

      // Outer eye corners (canonical MediaPipe face mesh indices).
      var eyeA = landmarks[33];
      var eyeB = landmarks[263];

      var ax = eyeA.x * vw;
      var ay = eyeA.y * vh;
      var bx = eyeB.x * vw;
      var by = eyeB.y * vh;

      var eyeDistancePx = Math.hypot(bx - ax, by - ay);
      var midX = (ax + bx) / 2;
      var midY = (ay + by) / 2;

      // Video is mirrored via CSS (scaleX(-1)) for a natural selfie view,
      // so mirror the x coordinate to match what's on screen.
      var mirroredMidX = vw - midX;

      var stageRect = modal.querySelector('.tryon-modal__stage').getBoundingClientRect();
      var scaleToStage = stageRect.width / vw;

      var screenX = mirroredMidX * scaleToStage;
      var screenY = midY * scaleToStage;

      var glassesFullWidthPx = (eyeDistancePx * FRAME_TO_EYE_DISTANCE_RATIO) / bboxWFrac;
      var displayScale = (glassesFullWidthPx * scaleToStage) / glasses.naturalWidth;

      var roll = Math.atan2(by - ay, bx - ax);
      // Mirrored view: flip roll sign so tilt matches what's on screen.
      var rollDeg = -(roll * 180) / Math.PI;

      // Rough, conservative yaw estimate from eye depth difference — clamped
      // so a wrong sign or noisy reading never breaks the visual, only
      // slightly under/over-tilts it.
      var dz = (eyeB.z - eyeA.z) * vw;
      var yawDeg = Math.max(-12, Math.min(12, dz * 0.15));

      // Shift from the full image's geometric center to the visible frame's
      // bbox center (there's transparent margin around the cutout), in
      // on-screen pixels, so the frame itself — not the padded canvas —
      // lands on the eye midpoint.
      var offsetXPx = (bboxCxFrac - 0.5) * glasses.naturalWidth * displayScale;
      var offsetYPx = (bboxCyFrac - 0.5) * glasses.naturalHeight * displayScale;

      glasses.style.left = (screenX - offsetXPx) + 'px';
      glasses.style.top = (screenY - offsetYPx) + 'px';
      glasses.style.transform =
        'translate(-50%, -50%) ' +
        'perspective(700px) ' +
        'rotateZ(' + rollDeg + 'deg) ' +
        'rotateY(' + yawDeg + 'deg) ' +
        'scale(' + displayScale + ')';

      rafId = requestAnimationFrame(frame);
    }

    rafId = requestAnimationFrame(frame);
  }

  function closeModal(modal) {
    if (currentModal === modal) currentModal = null;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = null;
    if (stream) {
      stream.getTracks().forEach(function (track) {
        track.stop();
      });
      stream = null;
    }
    var video = modal.querySelector('.tryon-video');
    video.srcObject = null;
    modal.hidden = true;
    document.documentElement.style.overflow = '';
  }

  function init() {
    var openButtons = document.querySelectorAll('[data-tryon-open]');
    openButtons.forEach(function (button) {
      var wrap = button.closest('.tryon-trigger-wrap');
      var modal = wrap && wrap.nextElementSibling;
      if (!modal || !modal.hasAttribute('data-tryon-modal')) return;
      button.addEventListener('click', function () {
        openModal(modal);
      });
      modal.querySelectorAll('[data-tryon-close]').forEach(function (closeEl) {
        closeEl.addEventListener('click', function () {
          closeModal(modal);
        });
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) closeModal(modal);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
